import type {
  GeometryCorner,
  GeometryQuad,
  LiveRawWarmStart,
  GeometryVerificationResult,
  MultiLabelGuideIntent,
} from "../types/liveGeometry";

export const LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION = "live-raw-geometry-verify-policy-v1" as const;

export const LIVE_RAW_GEOMETRY_VERIFY_THRESHOLDS = Object.freeze({
  multiLabelGuideMaxMeanCornerDistanceRatio: 0.18,
});

export type GeometryDetectionIntentMode =
  | "TRUSTED_SEED"
  | "GUIDED_FULL_DETECTION"
  | "FULL_DETECTION_NO_GUIDE";

export interface GeometryDetectionIntent {
  contract_version: "geometry-detection-intent-v1";
  policy_version: typeof LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION;
  mode: GeometryDetectionIntentMode;
  guide_intent: MultiLabelGuideIntent;
  seed_quad: GeometryQuad | null;
  guide_quad: GeometryQuad | null;
  target_confidence: number;
  preserve_guide_on_fallback: boolean;
  reason: string;
}

export interface GeometryWarmStartExecution {
  contract_version: "geometry-warmstart-execution-v1";
  policy_version: typeof LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION;
  requested_mode: GeometryDetectionIntentMode;
  guide_intent: MultiLabelGuideIntent;
  fast_seed_attempted: boolean;
  fast_seed_accepted: boolean;
  forced_full_detection_fallback: boolean;
  guide_preserved_on_fallback: boolean;
  multi_label_guide_active: boolean;
  cnn_guide_distance_ratio: number | null;
  cnn_fast_path_suppressed_by_guide: boolean;
  first_pass_method: string | null;
  final_method: string | null;
  reason: string;
}

type SearchRegion = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type DetectionSummary = {
  found: boolean;
  detectionMethod: string;
  finalGeometry: { accepted: boolean };
  geometryWarmStartExecution?: GeometryWarmStartExecution;
};

const clampSearchCoordinate = (value: number) => Math.max(-0.15, Math.min(1.15, value));

export function buildGeometryDetectionIntent(warmStart: LiveRawWarmStart | null): GeometryDetectionIntent {
  if (
    warmStart?.trust === "TRUSTED"
    && warmStart.can_seed_fast_path
    && warmStart.mapped_anchor_quad
  ) {
    return {
      contract_version: "geometry-detection-intent-v1",
      policy_version: LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
      mode: "TRUSTED_SEED",
      guide_intent: "LIVE_MAPPED_ANCHOR",
      seed_quad: warmStart.mapped_anchor_quad,
      guide_quad: warmStart.guide_quad ?? warmStart.mapped_anchor_quad,
      target_confidence: warmStart.combined_confidence,
      preserve_guide_on_fallback: true,
      reason: "TRUSTED_LIVE_RAW_SEED_REQUIRES_VERIFY_REFINE",
    };
  }

  if (warmStart?.trust === "GUIDED" && warmStart.can_bias_search && warmStart.guide_quad) {
    return {
      contract_version: "geometry-detection-intent-v1",
      policy_version: LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
      mode: "GUIDED_FULL_DETECTION",
      guide_intent: "PREVIEW_MAPPING_GUIDE",
      seed_quad: null,
      guide_quad: warmStart.guide_quad,
      target_confidence: warmStart.combined_confidence,
      preserve_guide_on_fallback: true,
      reason: "GUIDE_ONLY_FULL_DETECTION",
    };
  }

  return {
    contract_version: "geometry-detection-intent-v1",
    policy_version: LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
    mode: "FULL_DETECTION_NO_GUIDE",
    guide_intent: "NONE",
    seed_quad: null,
    guide_quad: null,
    target_confidence: 0,
    preserve_guide_on_fallback: false,
    reason: "NO_VALID_WARM_START",
  };
}

export function rawNormalizedQuadToSearchRegionCorners(
  quad: GeometryQuad | null,
  searchRegion: SearchRegion,
): GeometryCorner[] | undefined {
  if (!quad || quad.space !== "FULL_RAW_NORMALIZED" || quad.coordinate_unit !== "NORMALIZED_0_1") return undefined;
  if (!(searchRegion.width > 0) || !(searchRegion.height > 0)) return undefined;
  return quad.corners.map((corner) => ({
    x: clampSearchCoordinate((corner.x - searchRegion.left) / searchRegion.width),
    y: clampSearchCoordinate((corner.y - searchRegion.top) / searchRegion.height),
  }));
}

export function meanCornerDistanceRatio(
  first: readonly GeometryCorner[] | undefined,
  second: readonly GeometryCorner[] | undefined,
): number | null {
  if (!first || !second || first.length !== 4 || second.length !== 4) return null;
  const sum = first.reduce((total, point, index) => {
    const other = second[index];
    return total + Math.hypot(point.x - other.x, point.y - other.y) / Math.SQRT2;
  }, 0);
  return sum / 4;
}

export function finalizeGeometryVerification(
  intent: GeometryDetectionIntent,
  detection: DetectionSummary,
): GeometryVerificationResult {
  const execution = detection.geometryWarmStartExecution;
  const accepted = detection.found && detection.finalGeometry.accepted;

  if (intent.mode === "TRUSTED_SEED" && execution?.fast_seed_attempted && execution.fast_seed_accepted && accepted) {
    return {
      contract_version: "geometry-verify-refine-v1",
      policy_version: LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
      decision: "FAST_SEED_VERIFIED",
      verified: true,
      refined: detection.detectionMethod === "cnn-fast-refine",
      forced_full_detection_fallback: false,
      guide_intent: intent.guide_intent,
      guide_preserved_on_fallback: false,
      multi_label_guide_active: execution.multi_label_guide_active,
      result_method: detection.detectionMethod,
      reason: "TRUSTED_SEED_CONFIRMED_BY_OPENCV_SAFETY_GATES",
      provenance: { ...execution },
    };
  }

  if (intent.mode === "TRUSTED_SEED") {
    return {
      contract_version: "geometry-verify-refine-v1",
      policy_version: LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
      decision: "FULL_DETECTION_FALLBACK",
      verified: accepted,
      refined: false,
      forced_full_detection_fallback: execution?.forced_full_detection_fallback === true,
      guide_intent: intent.guide_intent,
      guide_preserved_on_fallback: execution?.guide_preserved_on_fallback === true,
      multi_label_guide_active: execution?.multi_label_guide_active === true,
      result_method: detection.detectionMethod,
      reason: accepted ? "TRUSTED_SEED_REJECTED_FULL_DETECTION_RECOVERED" : "TRUSTED_SEED_REJECTED_FULL_DETECTION_FAILED",
      provenance: execution ? { ...execution } : {},
    };
  }

  if (intent.mode === "GUIDED_FULL_DETECTION") {
    return {
      contract_version: "geometry-verify-refine-v1",
      policy_version: LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
      decision: "GUIDED_FULL_DETECTION",
      verified: accepted,
      refined: false,
      forced_full_detection_fallback: execution?.forced_full_detection_fallback === true,
      guide_intent: intent.guide_intent,
      guide_preserved_on_fallback: execution?.guide_preserved_on_fallback === true,
      multi_label_guide_active: execution?.multi_label_guide_active === true,
      result_method: detection.detectionMethod,
      reason: accepted ? "GUIDED_FULL_DETECTION_ACCEPTED" : "GUIDED_FULL_DETECTION_FAILED",
      provenance: execution ? { ...execution } : {},
    };
  }

  return {
    contract_version: "geometry-verify-refine-v1",
    policy_version: LIVE_RAW_GEOMETRY_VERIFY_POLICY_VERSION,
    decision: "FULL_DETECTION_NO_GUIDE",
    verified: accepted,
    refined: false,
    forced_full_detection_fallback: execution?.forced_full_detection_fallback === true,
    guide_intent: "NONE",
    guide_preserved_on_fallback: false,
    multi_label_guide_active: false,
    result_method: detection.detectionMethod,
    reason: accepted ? "FULL_DETECTION_ACCEPTED" : "FULL_DETECTION_FAILED",
    provenance: execution ? { ...execution } : {},
  };
}
